import { createFileRoute } from "@tanstack/react-router";
import {
  Phone,
  MapPin,
  Clock,
  Mail,
  Star,
  Sparkles,
  Users,
  Store,
  ArrowUpRight,
  Navigation,
} from "lucide-react";
import { Header } from "@/components/glam/Header";
import { Footer } from "@/components/glam/Footer";
import { LazyContactForm, LazyGallery } from "@/components/glam/LazySection";
import { Reveal, MediaFrame } from "@/components/glam/Reveal";
import { Picture } from "@/components/glam/Picture";
import { SalonReel } from "@/components/glam/SalonReel";
import { useSalonAgentTools } from "@/lib/webmcp";
import {
  ADDRESS_LINE,
  PLAZA_NAME,
  LOGO,
  LOGO_SRC,
  PHOTO_GUESTS,
  PHOTO_SILVER_BOB,
  PHOTO_STOREFRONT,
  PHOTO_WAITING,
  PHOTO_CELINA_WEFTS,
  PHOTO_CELINA_RESULT,
  CELINA_LOGO,
  CELINA_BOOKING_URL,
  REELS,
  BOOKING_URL,
  DIRECTIONS_URL,
  EMAIL,
  EXTENSION_METHODS,
  GOOGLE_REVIEWS_URL,
  HOURS,
  MAPS_URL,
  MAP_EMBED_URL,
  INSTAGRAM_URL,
  TIKTOK_URL,
  PACKAGES,
  PHONE_DISPLAY,
  PHONE_HREF,
  REVIEWS,
  SERVICES,
  SITE_URL,
  absoluteUrl,
} from "@/components/glam/data";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "HairSalon",
  name: "The Glam Room",
  description:
    "Luxury hair salon and hair extension bar in Wellington, Florida, specializing in five hair extension methods.",
  telephone: "+1-561-855-7493",
  email: EMAIL,
  url: SITE_URL,
  image: [absoluteUrl(PHOTO_GUESTS.src), absoluteUrl(LOGO_SRC)],
  logo: absoluteUrl(LOGO_SRC),
  sameAs: [INSTAGRAM_URL, TIKTOK_URL],
  address: {
    "@type": "PostalAddress",
    streetAddress: "8961 Hypoluxo Rd",
    addressLocality: "Wellington",
    addressRegion: "FL",
    postalCode: "33467",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 26.5806, longitude: -80.1739 },
  areaServed: ["Wellington, FL", "Lake Worth, FL", "West Boynton Beach, FL"],
  priceRange: "$$",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Tuesday", "Thursday"],
      opens: "10:00",
      closes: "19:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Wednesday", "Friday", "Saturday"],
      opens: "09:00",
      closes: "17:00",
    },
  ],
};

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "Hair Extensions Salon in Wellington, FL | The Glam Room" },
      {
        name: "description",
        content:
          "The Glam Room is Wellington's luxury hair salon and extension bar, specializing in five hair extension methods. Serving Lake Worth and West Boynton. Book today.",
      },
      {
        property: "og:title",
        content: "Hair Extensions Salon in Wellington, FL | The Glam Room",
      },
      {
        property: "og:description",
        content:
          "Wellington's luxury hair salon and on-site extension bar — five extension methods, expertly applied. Serving Lake Worth and West Boynton.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/` },
      { property: "og:image", content: absoluteUrl(PHOTO_GUESTS.src) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absoluteUrl(PHOTO_GUESTS.src) },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/` },
      {
        rel: "preload",
        as: "image",
        href: "/media/photo1-1400.avif",
        type: "image/avif",
        imageSrcSet: PHOTO_GUESTS.srcSetAvif,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
  }),
});

const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-full bg-charcoal px-7 py-3.5 text-[0.78rem] font-medium tracking-[0.16em] uppercase text-cream shadow-sm transition-all hover:-translate-y-0.5 hover:bg-charcoal/90 hover:shadow-xl";
const btnOutline =
  "inline-flex items-center justify-center gap-2 rounded-full border border-current px-7 py-3.5 text-[0.78rem] tracking-[0.16em] uppercase transition-all hover:-translate-y-0.5";

function Index() {
  useSalonAgentTools();

  return (
    <div id="top" className="bg-background">
      <Header />
      <main id="main-content" tabIndex={-1}>
        {/* ── Hero ───────────────────────────────────────────── */}
        <section className="relative flex min-h-[100svh] items-center">
          <MediaFrame className="absolute inset-0">
            <Picture
              image={PHOTO_GUESTS}
              alt="Three guests with long, glossy hair styled with luxury extensions at The Glam Room"
              sizes="100vw"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              imgClassName="h-full w-full object-cover"
            />
          </MediaFrame>
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-b from-charcoal/85 via-charcoal/60 to-charcoal/90"
          />
          <div
            aria-hidden="true"
            className="hairline pointer-events-none absolute inset-0 opacity-60"
          />
          <div className="relative mx-auto w-full max-w-4xl px-5 pt-28 pb-20 text-center lg:px-8">
            <Picture
              image={LOGO}
              alt="The Glam Room"
              sizes="(min-width: 640px) 128px, 96px"
              imgClassName="float-slow mx-auto mb-8 h-24 w-auto brightness-0 invert sm:h-32"
            />
            <p className="text-[0.7rem] tracking-[0.34em] text-gold uppercase">
              Wellington · Lake Worth · West Boynton
            </p>
            <h1 className="mt-6 font-display text-5xl leading-[1.05] text-cream sm:text-6xl lg:text-7xl">
              Luxury Hair Extensions &amp; Hair Salon in Wellington, FL
            </h1>
            <p className="mt-4 font-display text-2xl text-gold sm:text-3xl">
              Come for the Glam, Stay for the Vibe
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-cream/85 sm:text-lg">
              Wellington's home for luxury hair extensions — five methods, expertly applied, with an
              on-site extension bar you won't find anywhere else.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={btnPrimary}
              >
                Book Your Appointment
              </a>
              <a href={PHONE_HREF} className={`${btnOutline} text-cream hover:bg-cream/10`}>
                <Phone className="h-4 w-4" aria-hidden="true" />
                Call {PHONE_DISPLAY}
              </a>
            </div>
            <p className="mt-8 text-[0.78rem] tracking-[0.1em] text-cream/75">
              <span className="text-gold" aria-hidden="true">
                ★★★★★
              </span>{" "}
              Loved by our Google reviewers · Family owned · 20+ years of experience
            </p>
          </div>
        </section>

        {/* ── Trust bar ──────────────────────────────────────── */}
        <section
          aria-label="Why guests choose us"
          className="surface-sand grain border-y border-border"
        >
          <ul className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-5 px-5 py-7 lg:grid-cols-4 lg:px-8">
            {[
              { icon: Sparkles, label: "5 Extension Methods" },
              { icon: Store, label: "On-Site Extension Bar" },
              { icon: Users, label: "Family Owned" },
              { icon: MapPin, label: "Wellington · Lake Worth · West Boynton" },
            ].map(({ icon: Icon, label }) => (
              <li key={label} className="flex min-w-0 items-center gap-3">
                <Icon
                  className="h-5 w-5 shrink-0 text-gold-deep"
                  strokeWidth={1.4}
                  aria-hidden="true"
                />
                <span className="text-[0.72rem] tracking-[0.13em] text-foreground uppercase">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Extensions spotlight ───────────────────────────── */}
        <section id="extensions" className="surface-cream grain py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
              <Reveal variant="left" className="overflow-hidden rounded-3xl shadow-xl">
                <MediaFrame>
                  <Picture
                    image={PHOTO_SILVER_BOB}
                    alt="Back view of a silver-blonde curled bob styled at The Glam Room"
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    imgClassName="h-full w-full object-cover"
                  />
                </MediaFrame>
              </Reveal>
              <Reveal delay={120}>
                <p className="eyebrow">Our Signature Service</p>
                <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
                  Luxury Hair Extensions, Done Right
                </h2>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                  We specialize in five extension methods, and we match the method to you — your
                  hair texture, your density, your day-to-day. Whether you're after length, volume,
                  or both, the goal is the same: extensions that look and feel like they grew there.
                </p>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  Every set starts with a consultation, an honest conversation about maintenance,
                  and a color match made with hair we keep in stock right here in the salon.
                </p>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${btnPrimary} mt-8`}
                >
                  Book an Extension Consultation
                </a>
              </Reveal>
            </div>

            <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {EXTENSION_METHODS.map((m, i) => (
                <Reveal
                  key={m.name}
                  delay={i * 80}
                  className="group overflow-hidden rounded-2xl border border-border bg-card transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-xl"
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <Picture
                      image={m.image}
                      alt={m.imageAlt}
                      sizes="(min-width: 1024px) 20vw, (min-width: 640px) 50vw, 50vw"
                      imgClassName="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl">{m.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.blurb}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── The Celina Collection ──────────────────────────── */}
        <section
          id="celina-collection"
          className="surface-charcoal grain hairline py-24 text-cream sm:py-32"
        >
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-20 lg:px-8">
            <Reveal>
              <Picture
                image={CELINA_LOGO}
                alt="The Celina Collection logo"
                sizes="160px"
                imgClassName="mb-8 h-32 w-32 rounded-full bg-cream object-contain p-2 shadow-xl ring-1 ring-gold/40 sm:h-40 sm:w-40"
              />
              <p className="text-[0.72rem] tracking-[0.28em] text-gold uppercase">
                A Brand of Our Own
              </p>
              <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">The Celina Collection</h2>
              <p className="mt-4 font-display text-2xl text-cream/80 italic">
                Our own hair extension line — and the only extension bar in the area.
              </p>
              <p className="mt-6 text-base leading-relaxed text-cream/75">
                We keep our hair in stock on site, so there's no waiting on shipping and no guessing
                from a swatch on a screen. You can see it, hold it, and hold it against your own
                hair before you commit to anything.
              </p>
              <p className="mt-4 text-base leading-relaxed text-cream/75">
                We also sell The Celina Collection wholesale to other stylists in the area — if you
                work behind the chair nearby, we'd love to talk.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a
                  href={CELINA_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={btnPrimary}
                >
                  Shop the Extension Bar
                </a>
                <a
                  href={`mailto:${EMAIL}?subject=Stylist%20Inquiry%20—%20The%20Celina%20Collection`}
                  className={`${btnOutline} text-cream hover:bg-cream/10`}
                >
                  Stylist Inquiries
                </a>
              </div>
            </Reveal>
            <Reveal variant="zoom" delay={120} className="grid gap-5">
              <div className="overflow-hidden rounded-3xl shadow-2xl">
                <MediaFrame>
                  <Picture
                    image={PHOTO_CELINA_RESULT}
                    alt="Long blonde hair styled in soft curls with dimensional highlights"
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    imgClassName="aspect-[4/5] w-full object-cover sm:aspect-[3/4]"
                  />
                </MediaFrame>
              </div>
              <div className="overflow-hidden rounded-3xl shadow-2xl">
                <MediaFrame>
                  <Picture
                    image={PHOTO_CELINA_WEFTS}
                    alt="Long dark hair with a discreet extension row and glossy curls"
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    imgClassName="aspect-[4/5] w-full object-cover sm:aspect-[3/4]"
                  />
                </MediaFrame>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── In Motion ──────────────────────────────────────── */}
        <section className="surface-cream grain hairline py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal className="max-w-2xl">
              <p className="eyebrow">In the Chair</p>
              <h2 className="mt-4 text-4xl sm:text-5xl">The Glam Room in Motion</h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                A glimpse of the work as it happens — hair, hands, and finished movement.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {REELS.map((reel, i) => (
                <Reveal key={reel.src} delay={i * 120} variant="zoom">
                  <figure className="overflow-hidden rounded-3xl shadow-xl">
                    <SalonReel src={reel.src} caption={reel.caption} captionsSrc={reel.captionsSrc} />
                    <figcaption className="sr-only">{reel.caption}</figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Services ───────────────────────────────────────── */}
        <section id="services" className="surface-sand grain py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal className="max-w-2xl">
              <p className="eyebrow">The Menu</p>
              <h2 className="mt-4 text-4xl sm:text-5xl">Everything Under One Roof</h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                A full-service salon, led by extensions. Tell us what you're after and we'll build
                the right service around it.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {SERVICES.map((s, i) => (
                <Reveal
                  key={s.name}
                  delay={(i % 4) * 70}
                  className={
                    s.featured
                      ? "rounded-2xl border border-gold bg-card/80 p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl sm:col-span-2"
                      : "rounded-2xl border border-border bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
                  }
                >
                  {s.featured && <p className="eyebrow">Most Requested</p>}
                  <h3 className={s.featured ? "mt-3 text-3xl" : "text-2xl"}>{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.blurb}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Packages ───────────────────────────────────────── */}
        <section id="packages" className="surface-cream grain hairline py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal className="max-w-2xl">
              <p className="eyebrow">Bundled Care</p>
              <h2 className="mt-4 text-4xl sm:text-5xl">Packages</h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Thoughtful groupings that keep your hair looking its best between visits. Ask us
                what fits your hair and your schedule.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {PACKAGES.map((p, i) => (
                <Reveal
                  key={p.name}
                  delay={i * 90}
                  className="flex flex-col rounded-2xl border border-border bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-xl"
                >
                  <h3 className="text-3xl">{p.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {p.blurb}
                  </p>
                  <a
                    href="#contact"
                    className="mt-7 self-start border-b border-gold/50 pb-0.5 text-sm tracking-wide text-gold-deep transition-colors hover:border-gold-deep hover:text-foreground"
                  >
                    Ask about this piece →
                  </a>

                </Reveal>
              ))}
            </div>
            <Reveal className="mt-8 rounded-2xl border border-dashed border-gold bg-background px-6 py-5 text-center">
              <p className="text-sm tracking-wide text-foreground">
                Blow-dry loyalty: buy five blowouts and your sixth is on us.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ── About ──────────────────────────────────────────── */}
        <section id="about" className="surface-sand grain py-24 sm:py-32">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-20 lg:px-8">
            <Reveal variant="left" className="overflow-hidden rounded-3xl shadow-xl">
              <MediaFrame>
                <Picture
                  image={PHOTO_WAITING}
                  alt="The warm, boho-chic waiting area inside The Glam Room in Wellington, Florida"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  imgClassName="h-full w-full object-cover"
                />
              </MediaFrame>
            </Reveal>
            <Reveal delay={120}>
              <p className="eyebrow">Our Story</p>
              <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">A Vision Brought to Life</h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                The Glam Room was born from a passion for beautiful hair, elevated artistry, and the
                belief that confidence begins with how you feel.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                After more than two decades behind the chair, my husband encouraged me to turn my
                passion into something of my own. With his unwavering support, The Glam Room became
                more than a salon—it became a vision brought to life.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Today, that story feels even more meaningful as I work alongside my daughter, who
                followed in my footsteps and discovered her own love for the beauty industry. What
                began as a personal dream has evolved into a family legacy.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                As we’ve grown, so has our brand. From dimensional color and luxury hair extensions
                to the creation of our own extension line, The Celina Collection, every detail
                reflects our commitment to quality, artistry, and an elevated experience.
              </p>
            </Reveal>
          </div>
        </section>

        <LazyGallery />

        {/* ── Reviews ────────────────────────────────────────── */}
        <section id="reviews" className="surface-sand grain hairline py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal className="max-w-2xl">
              <p className="eyebrow">Kind Words</p>
              <h2 className="mt-4 text-4xl sm:text-5xl">What Our Guests Say</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {REVIEWS.map((r, i) => (
                <Reveal
                  key={r.quote}
                  delay={(i % 2) * 90}
                  className="rounded-2xl border border-border bg-card p-8"
                >
                  <p className="flex gap-1 text-gold">
                    <span className="sr-only">Rated 5 out of 5 stars</span>
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star
                        key={s}
                        className="h-4 w-4 fill-gold text-gold"
                        strokeWidth={1}
                        aria-hidden="true"
                      />
                    ))}
                  </p>
                  <blockquote className="mt-5 font-display text-xl leading-snug text-foreground sm:text-2xl">
                    “{r.quote}”
                  </blockquote>
                  <p className="mt-5 text-[0.72rem] tracking-[0.2em] text-muted-foreground uppercase">
                    {r.name}
                  </p>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-10 text-center">
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${btnOutline} text-gold-deep hover:bg-gold-deep hover:text-primary-foreground`}
              >
                Read All Our Google Reviews
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </Reveal>
          </div>
        </section>

        {/* ── Find Us ────────────────────────────────────────── */}
        <section id="find-us" className="surface-cream grain py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal className="max-w-2xl">
              <p className="eyebrow">Find Us</p>
              <h2 className="mt-4 text-4xl sm:text-5xl">Easy to Find — We're in the Plaza</h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                You’ll find us inside <span className="text-foreground">{PLAZA_NAME}</span> on
                Hypoluxo Road — look for the storefront rather than a standalone building, then park
                in the plaza lot and head straight in.
              </p>
            </Reveal>

            <Reveal variant="zoom" className="mt-10 overflow-hidden rounded-3xl shadow-xl">
              <Picture
                image={PHOTO_STOREFRONT}
                alt="The Glam Room storefront with its blue arched facade and wooden benches"
                sizes="100vw"
                imgClassName="h-[340px] w-full object-cover object-center sm:h-[520px]"
              />
            </Reveal>

            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              <Reveal className="overflow-hidden rounded-3xl border border-border">
                <iframe
                  src={MAP_EMBED_URL}
                  title="Map showing The Glam Room at 8961 Hypoluxo Rd, Wellington, FL 33467"
                  loading="lazy"
                  width={600}
                  height={450}
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-[380px] w-full lg:h-full lg:min-h-[460px]"
                />
              </Reveal>

              <Reveal delay={120} className="rounded-3xl border border-border bg-card p-8">
                <h3 className="text-3xl">Visit The Glam Room</h3>
                <ul className="mt-6 space-y-4 text-sm">
                  <li className="flex gap-3">
                    <MapPin
                      className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep"
                      strokeWidth={1.4}
                      aria-hidden="true"
                    />
                    <div>
                      <a
                        href={MAPS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground underline-offset-4 hover:underline"
                      >
                        {ADDRESS_LINE}
                      </a>
                      <p className="mt-1 text-muted-foreground">{PLAZA_NAME}</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <Phone
                      className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep"
                      strokeWidth={1.4}
                      aria-hidden="true"
                    />
                    <a
                      href={PHONE_HREF}
                      className="text-foreground underline-offset-4 hover:underline"
                    >
                      {PHONE_DISPLAY}
                    </a>
                  </li>
                  <li className="flex gap-3">
                    <Mail
                      className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep"
                      strokeWidth={1.4}
                      aria-hidden="true"
                    />
                    <a
                      href={`mailto:${EMAIL}`}
                      className="text-foreground underline-offset-4 hover:underline"
                    >
                      {EMAIL}
                    </a>
                  </li>
                </ul>

                <h4 className="mt-8 flex items-center gap-2 text-xl">
                  <Clock className="h-4 w-4 text-gold-deep" strokeWidth={1.4} aria-hidden="true" />
                  Hours
                </h4>
                <table className="mt-3 w-full text-sm">
                  <caption className="sr-only">The Glam Room weekly hours</caption>
                  <tbody>
                    {HOURS.map((h) => (
                      <tr key={h.day} className="border-b border-border/60 last:border-0">
                        <th
                          scope="row"
                          className="py-2 text-left font-normal text-muted-foreground"
                        >
                          {h.day}
                        </th>
                        <td className="py-2 text-right text-foreground">{h.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <a
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${btnPrimary} mt-8`}
                >
                  <Navigation className="h-4 w-4" aria-hidden="true" />
                  Get Directions
                </a>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ── Contact Form ───────────────────────────────────── */}
        <section id="contact" className="surface-sand grain py-24 sm:py-32">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
            <Reveal>
              <p className="eyebrow">Contact</p>
              <h2 className="mt-4 text-4xl sm:text-5xl">Send Us a Message</h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Questions about extensions, color, or which service is right for you? Send a note
                and we'll get back to you personally. For the fastest response, book online or give
                us a call.
              </p>
              <ul className="mt-8 space-y-4 text-sm">
                <li className="flex gap-3">
                  <Phone
                    className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep"
                    strokeWidth={1.4}
                    aria-hidden="true"
                  />
                  <a
                    href={PHONE_HREF}
                    className="text-foreground underline-offset-4 hover:underline"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </li>
                <li className="flex gap-3">
                  <Mail
                    className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep"
                    strokeWidth={1.4}
                    aria-hidden="true"
                  />
                  <a
                    href={`mailto:${EMAIL}`}
                    className="text-foreground underline-offset-4 hover:underline"
                  >
                    {EMAIL}
                  </a>
                </li>
                <li className="flex gap-3">
                  <MapPin
                    className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep"
                    strokeWidth={1.4}
                    aria-hidden="true"
                  />
                  <span>
                    {ADDRESS_LINE}
                    <span className="mt-1 block text-muted-foreground">{PLAZA_NAME}</span>
                  </span>
                </li>
              </ul>
            </Reveal>

            <Reveal
              delay={120}
              className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8"
            >
              <LazyContactForm />
            </Reveal>
          </div>
        </section>

        {/* ── Final CTA ──────────────────────────────────────── */}
        <section className="surface-gold grain py-20 text-center text-primary-foreground sm:py-24">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-4xl sm:text-5xl">Ready for Your Glam Moment?</h2>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-cream px-7 py-3.5 text-[0.78rem] tracking-[0.16em] text-charcoal uppercase transition-all hover:-translate-y-0.5 hover:shadow-xl"
              >
                Book Your Appointment
              </a>
              <a
                href={PHONE_HREF}
                className={`${btnOutline} text-primary-foreground hover:bg-cream/15`}
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {PHONE_DISPLAY}
              </a>
            </div>
            <p className="mt-6 text-sm text-primary-foreground/85">We respond right away.</p>
          </div>
        </section>
      </main>

      <Footer />

      {/* ── Sticky mobile CTA bar ────────────────────────────── */}
      <nav
        aria-label="Quick actions"
        className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-2 gap-2 border-t border-border bg-background/95 p-3 backdrop-blur-md sm:hidden"
      >
        <a
          href={PHONE_HREF}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-gold-deep text-[0.75rem] tracking-[0.14em] text-gold-deep uppercase"
        >
          <Phone className="h-4 w-4" aria-hidden="true" />
          Call
        </a>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-charcoal text-[0.75rem] font-medium tracking-[0.14em] text-cream uppercase"
        >
          Book
        </a>
      </nav>
    </div>
  );
}
